import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import {
  InstructorUpcomingSlot,
  cancelSubstitution,
  createSubstitution,
  getInstructorUpcomingSlots,
} from '../../_services/SubstitutionService';
import { Instructor, searchInstructors } from '../../_services/schedules.service';

const today = (): string => new Date().toISOString().slice(0, 10);
const addDays = (iso: string, days: number): string => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

interface SubstituteState {
  open: boolean;
  slot: InstructorUpcomingSlot | null;
  startDate: string;
  endDate: string;
  reason: string;
  search: string;
  results: Instructor[];
  searching: boolean;
  selected: Instructor | null;
  saving: boolean;
  error: string | null;
}

const initialSubState: SubstituteState = {
  open: false,
  slot: null,
  startDate: today(),
  endDate: today(),
  reason: '',
  search: '',
  results: [],
  searching: false,
  selected: null,
  saving: false,
  error: null,
};

const TeacherSchedulePage: React.FC = () => {
  const { instructorId: instructorIdParam } = useParams<{ instructorId: string }>();
  const instructorId = parseInt(instructorIdParam, 10);

  const [startDate, setStartDate] = useState<string>(today());
  const [endDate, setEndDate] = useState<string>(addDays(today(), 30));
  const [slots, setSlots] = useState<InstructorUpcomingSlot[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [substituteState, setSubstituteState] =
    useState<SubstituteState>(initialSubState);
  const [instructorName, setInstructorName] = useState<string>('');

  const fetchSlots = useCallback(async () => {
    if (!instructorId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getInstructorUpcomingSlots(
        instructorId,
        startDate,
        endDate,
      );
      setSlots(data);
      const firstWithName = data.find((s) => s.instructorName);
      if (firstWithName?.instructorName) {
        setInstructorName(firstWithName.instructorName);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [instructorId, startDate, endDate]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  // Search instructors as user types
  useEffect(() => {
    const search = substituteState.search.trim();
    if (!substituteState.open || search.length < 2) {
      setSubstituteState((prev) => ({
        ...prev,
        results: [],
        searching: false,
      }));
      return;
    }
    let cancelled = false;
    setSubstituteState((prev) => ({ ...prev, searching: true }));
    const timer = setTimeout(async () => {
      try {
        const res = await searchInstructors(search);
        if (cancelled) return;
        const list = (res?.data ?? []).filter((i) => i.id !== instructorId);
        setSubstituteState((prev) => ({
          ...prev,
          results: list,
          searching: false,
        }));
      } catch {
        if (!cancelled) {
          setSubstituteState((prev) => ({
            ...prev,
            results: [],
            searching: false,
          }));
        }
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [substituteState.search, substituteState.open, instructorId]);

  const openSubstituteFor = (slot: InstructorUpcomingSlot) => {
    const slotDate = (slot.date || '').slice(0, 10) || today();
    setSubstituteState({
      ...initialSubState,
      open: true,
      slot,
      startDate: slotDate,
      endDate: slotDate,
    });
  };

  const submitAssign = async () => {
    if (!substituteState.slot) return;
    if (!substituteState.selected) {
      setSubstituteState((prev) => ({
        ...prev,
        error: 'Pick a substitute instructor',
      }));
      return;
    }
    if (
      new Date(substituteState.startDate) > new Date(substituteState.endDate)
    ) {
      setSubstituteState((prev) => ({
        ...prev,
        error: 'Start date must be on or before end date',
      }));
      return;
    }
    try {
      setSubstituteState((prev) => ({ ...prev, saving: true, error: null }));
      await createSubstitution({
        activitySlotTemplateId: substituteState.slot.templateId,
        startDate: substituteState.startDate,
        endDate: substituteState.endDate,
        substituteInstructorId: substituteState.selected.id,
        reason: substituteState.reason || undefined,
        status: 'APPROVED',
      });
      setSubstituteState(initialSubState);
      await fetchSlots();
    } catch (e: any) {
      setSubstituteState((prev) => ({
        ...prev,
        saving: false,
        error:
          e?.response?.data?.message || e?.message || 'Failed to save',
      }));
    }
  };

  const cancelExisting = async (subId: number) => {
    if (!window.confirm('Cancel this substitution?')) return;
    try {
      await cancelSubstitution(subId);
      await fetchSlots();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to cancel');
    }
  };

  const formatDate = (d: string | null): string => {
    if (!d) return '—';
    return String(d).slice(0, 10);
  };

  return (
    <div style={{ padding: '20px 24px' }}>
      <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--border-default)' }}>
        <h4 style={{ margin: 0, fontWeight: 600 }}>
          Teacher Schedule
          {instructorName ? <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> — {instructorName}</span> : null}
        </h4>
        <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
          Instructor ID: {instructorId} &middot; Use this page to assign
          substitutes when this teacher is absent.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end' }}>
        <Form.Group>
          <Form.Label>From</Form.Label>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>To</Form.Label>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Form.Group>
        <Button variant="primary" onClick={fetchSlots} disabled={loading}>
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spinner animation="border" />
        </div>
      ) : slots.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          No upcoming classes for this instructor in the selected range.
        </div>
      ) : (
        <Table hover responsive>
          <thead>
            <tr>
              <th>Date</th>
              <th>Time</th>
              <th>Module</th>
              <th>Topic</th>
              <th>Room</th>
              <th>Substitute Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {slots.map((s) => (
              <tr key={`${s.templateId}-${s.date}`}>
                <td>{formatDate(s.date)}</td>
                <td>
                  {s.startTime || ''}
                  {s.endTime ? ` – ${s.endTime}` : ''}
                </td>
                <td>
                  {s.moduleCode}
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {s.moduleName}
                  </div>
                </td>
                <td>{s.topicName || '—'}</td>
                <td>{s.roomName || '—'}</td>
                <td>
                  {s.substitution ? (
                    <span>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          background:
                            s.substitution.status === 'APPROVED'
                              ? '#d4edda'
                              : '#fff3cd',
                          color:
                            s.substitution.status === 'APPROVED'
                              ? '#155724'
                              : '#856404',
                          marginRight: 6,
                        }}
                      >
                        {s.substitution.status}
                      </span>
                      {s.substitution.substituteInstructorName || ''}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--text-muted)' }}>None</span>
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {!s.hasApprovedSubstitute && !s.hasPendingRequest && (
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => openSubstituteFor(s)}
                    >
                      Assign Substitute
                    </Button>
                  )}
                  {s.substitution &&
                    (s.substitution.status === 'APPROVED' ||
                      s.substitution.status === 'REQUESTED') && (
                      <Button
                        size="sm"
                        variant="warning"
                        onClick={() => cancelExisting(s.substitution!.id)}
                      >
                        Cancel
                      </Button>
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal
        show={substituteState.open}
        onHide={() => setSubstituteState(initialSubState)}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Assign Substitute</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {substituteState.slot && (
            <div
              style={{
                padding: 8,
                background: 'var(--surface-page)',
                borderRadius: 4,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              <div>
                <strong>Slot:</strong>{' '}
                {substituteState.slot.templateName || `#${substituteState.slot.templateId}`}
              </div>
              <div>
                <strong>Time:</strong> {substituteState.slot.startTime || ''}
                {substituteState.slot.endTime
                  ? ` – ${substituteState.slot.endTime}`
                  : ''}
              </div>
              <div>
                <strong>Original:</strong>{' '}
                {substituteState.slot.instructorName || '—'}
              </div>
            </div>
          )}
          {substituteState.error && (
            <Alert variant="danger">{substituteState.error}</Alert>
          )}
          <Form>
            <div style={{ display: 'flex', gap: 12 }}>
              <Form.Group style={{ flex: 1 }}>
                <Form.Label>From</Form.Label>
                <Form.Control
                  type="date"
                  value={substituteState.startDate}
                  onChange={(e) =>
                    setSubstituteState((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                />
              </Form.Group>
              <Form.Group style={{ flex: 1 }}>
                <Form.Label>To</Form.Label>
                <Form.Control
                  type="date"
                  value={substituteState.endDate}
                  onChange={(e) =>
                    setSubstituteState((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }))
                  }
                />
              </Form.Group>
            </div>
            <Form.Group className="mt-2">
              <Form.Label>Reason (optional)</Form.Label>
              <Form.Control
                type="text"
                maxLength={500}
                placeholder="e.g. Sick leave"
                value={substituteState.reason}
                onChange={(e) =>
                  setSubstituteState((prev) => ({
                    ...prev,
                    reason: e.target.value,
                  }))
                }
              />
            </Form.Group>
            <Form.Group className="mt-2">
              <Form.Label>Substitute Instructor</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search by name or code (min 2 chars)"
                value={substituteState.search}
                onChange={(e) =>
                  setSubstituteState((prev) => ({
                    ...prev,
                    search: e.target.value,
                    selected: null,
                  }))
                }
              />
            </Form.Group>
            {substituteState.selected && (
              <div
                style={{
                  marginTop: 12,
                  padding: 8,
                  background: '#e8f5e9',
                  border: '1px solid #c8e6c9',
                  borderRadius: 4,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <span>Selected: {substituteState.selected.name}</span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    setSubstituteState((prev) => ({ ...prev, selected: null }))
                  }
                >
                  Change
                </Button>
              </div>
            )}
            {!substituteState.selected &&
              (substituteState.searching ||
                substituteState.results.length > 0) && (
                <div
                  style={{
                    marginTop: 8,
                    maxHeight: 200,
                    overflowY: 'auto',
                    border: '1px solid var(--border-default)',
                    borderRadius: 4,
                  }}
                >
                  {substituteState.searching && (
                    <div style={{ padding: 12, textAlign: 'center' }}>
                      <Spinner animation="border" size="sm" /> Searching…
                    </div>
                  )}
                  {!substituteState.searching &&
                    substituteState.results.map((i) => (
                      <button
                        key={i.id}
                        type="button"
                        onClick={() =>
                          setSubstituteState((prev) => ({
                            ...prev,
                            selected: i,
                          }))
                        }
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px 12px',
                          border: 'none',
                          borderBottom: '1px solid #f1f3f5',
                          background: 'white',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ fontWeight: 500 }}>{i.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {i.code ? `${i.code} · ` : ''}
                          {i.email || ''}
                        </div>
                      </button>
                    ))}
                </div>
              )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setSubstituteState(initialSubState)}
            disabled={substituteState.saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submitAssign}
            disabled={substituteState.saving}
          >
            {substituteState.saving ? (
              <>
                <Spinner as="span" size="sm" animation="border" /> Saving…
              </>
            ) : (
              'Assign Substitute'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TeacherSchedulePage;
